import { useState } from "react";
import { 
  Search, 
  Settings, 
  Bell,
  Plus,
  ChevronDown,
  Clock,
  X,
  Filter,
  FilterX,
  Trash2
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CreateCaseModal } from "./CreateCaseModal";
import { 
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "./ui/context-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

interface CaseData {
  id: string;
  title: string;
  type: string;
  urls: string[];
  createdAt: Date;
  status: "active" | "completed" | "pending";
}

interface AppSidebarProps {
  cases: CaseData[];
  activeCase: CaseData | null;
  onCreateCase: (caseData: { title: string; type: string; urls: string[] }) => Promise<void>;
  onSwitchCase: (caseData: CaseData) => void;
  onDeleteCase: (caseId: string) => Promise<void>;
  onOpenSettings: () => void;
}

const getCaseTypeLabel = (type: string): string => {
  const types: Record<string, string> = {
    "ncii": "NCII",
    "verified-csam": "V-CSAM",
    "suspected-csam": "S-CSAM", 
    "verified-trafficking": "V-HT",
    "test-case": "Test"
  };
  return types[type] || type;
};

const getCaseTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    "ncii": "bg-purple-500/10 text-purple-400 border-purple-500/20",
    "verified-csam": "bg-red-500/10 text-red-400 border-red-500/20",
    "suspected-csam": "bg-orange-500/10 text-orange-400 border-orange-500/20",
    "verified-trafficking": "bg-red-600/10 text-red-500 border-red-600/20",
    "test-case": "bg-blue-500/10 text-blue-400 border-blue-500/20"
  };
  return colors[type] || "bg-gray-500/10 text-gray-400 border-gray-500/20";
};

const caseTypeOptions = [
  { value: "all", label: "All Types" },
  { value: "ncii", label: "NCII" },
  { value: "verified-csam", label: "V-CSAM" },
  { value: "suspected-csam", label: "S-CSAM" },
  { value: "verified-trafficking", label: "V-HT" },
  { value: "test-case", label: "Test" }
];

const statusOptions = [
  { value: "all", label: "All Status" },
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "pending", label: "Pending" }
];

export function AppSidebar({ cases, activeCase, onCreateCase, onSwitchCase, onDeleteCase, onOpenSettings }: AppSidebarProps) {
  const [isWorkspaceOpen, setIsWorkspaceOpen] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [expandedSearchTerm, setExpandedSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState<CaseData | null>(null);

  const handleCreateCase = async (caseData: { title: string; type: string; urls: string[] }) => {
    await onCreateCase(caseData);
  };

  const handleDeleteClick = (caseItem: CaseData) => {
    setCaseToDelete(caseItem);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (caseToDelete) {
      try {
        await onDeleteCase(caseToDelete.id);
        setDeleteDialogOpen(false);
        setCaseToDelete(null);
      } catch (error) {
        console.error('Failed to delete case:', error);
        // You might want to show an error toast here
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCaseToDelete(null);
  };

  const filteredCases = cases.filter(caseItem => {
    // Text search filter
    const matchesSearch = expandedSearchTerm === "" || 
      caseItem.title.toLowerCase().includes(expandedSearchTerm.toLowerCase()) ||
      caseItem.id.toLowerCase().includes(expandedSearchTerm.toLowerCase());
    
    // Case type filter
    const matchesType = typeFilter === "all" || caseItem.type === typeFilter;
    
    // Status filter
    const matchesStatus = statusFilter === "all" || caseItem.status === statusFilter;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "active": return "bg-green-500 shadow-green-500/50";
      case "completed": return "bg-blue-500 shadow-blue-500/50";
      case "pending": return "bg-yellow-500 shadow-yellow-500/50";
      default: return "bg-gray-500 shadow-gray-500/50";
    }
  };

  const handleSearchIconClick = () => {
    setIsSearchExpanded(true);
  };

  const handleSearchClose = () => {
    setIsSearchExpanded(false);
    setExpandedSearchTerm("");
    setTypeFilter("all");
    setStatusFilter("all");
  };

  const handleClearFilters = () => {
    setExpandedSearchTerm("");
    setTypeFilter("all");
    setStatusFilter("all");
  };

  const hasActiveFilters = expandedSearchTerm !== "" || typeFilter !== "all" || statusFilter !== "all";
  const activeFilterCount = [
    expandedSearchTerm !== "",
    typeFilter !== "all", 
    statusFilter !== "all"
  ].filter(Boolean).length;

  const displayedCases = filteredCases;
  

  return (
    <>
      <div className="w-64 bg-sidebar border-r border-sidebar-border h-full flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-sidebar-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110 hover:shadow-lg hover:shadow-primary/25">
              <div className="w-4 h-4 bg-primary-foreground rounded transition-all duration-200" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-sidebar-foreground">Prune Copilot</h1>
              <p className="text-xs text-muted-foreground">Investigative AI</p>
            </div>
          </div>
        </div>

        {/* Primary Action Section */}
        <div className="p-4">
          {!isSearchExpanded ? (
            <div className="flex gap-2">
              {/* New Investigation Button */}
              <Button 
                onClick={() => setIsCreateModalOpen(true)}
                className="flex-1 justify-center gap-2 bg-primary hover:bg-primary text-primary-foreground shadow-sm transition-all duration-300 hover:shadow-xl hover:shadow-primary/30 hover:scale-[1.02] group relative overflow-hidden new-investigation-btn"
                size="default"
              >
                <Plus className="h-4 w-4 transition-all duration-300 group-hover:rotate-90 group-hover:scale-110 z-10" />
                <span className="z-10 transition-all duration-300 group-hover:tracking-wide">New Investigation</span>
                
                {/* Enhanced Multi-layer Shimmer Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary-foreground/30 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 ease-out" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary-foreground/10 to-transparent translate-x-[-150%] group-hover:translate-x-[150%] transition-transform duration-800 ease-out delay-100" />
                
                {/* Gradient Background Animation */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary opacity-100 group-hover:opacity-0 transition-opacity duration-300" />
                <div className="absolute inset-0 bg-gradient-to-r from-primary to-accent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                {/* Subtle Glow */}
                <div className="absolute -inset-1 bg-gradient-to-r from-primary/50 to-accent/50 opacity-0 group-hover:opacity-20 transition-opacity duration-500 blur-md -z-10" />
              </Button>

              {/* Search/Filter Icon Button */}
              <Button
                onClick={handleSearchIconClick}
                variant="outline"
                size="default"
                className="px-3 bg-background hover:bg-accent hover:border-primary transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-primary/20 group border-border hover:border-primary/50 relative"
                title="Search and filter cases"
              >
                {hasActiveFilters ? (
                  <Filter className="h-4 w-4 text-primary transition-all duration-300 group-hover:scale-110" />
                ) : (
                  <Search className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-all duration-300 group-hover:scale-110" />
                )}
                {activeFilterCount > 0 && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-medium">
                    {activeFilterCount}
                  </div>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-3 animate-in slide-in-from-top-2 duration-300">
              {/* Expanded Search Bar */}
              <div className="relative group">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground transition-all duration-200 group-hover:text-primary group-hover:scale-110" />
                <Input
                  type="text"
                  placeholder="Search cases..."
                  value={expandedSearchTerm}
                  onChange={(e) => setExpandedSearchTerm(e.target.value)}
                  className="pl-10 pr-10 bg-input-background border border-border rounded-lg text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 hover:border-primary/50 hover:shadow-sm focus:scale-[1.01]"
                  autoFocus
                />
                <Button
                  onClick={handleSearchClose}
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-muted transition-all duration-200 hover:scale-110 group"
                >
                  <X className="h-3 w-3 text-muted-foreground group-hover:text-foreground transition-colors duration-200" />
                </Button>
              </div>

              {/* Filter Controls */}
              <div className="grid grid-cols-2 gap-2">
                {/* Case Type Filter */}
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Case Type</label>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="h-8 text-xs bg-input-background border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {caseTypeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value} className="text-xs">
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Status</label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="h-8 text-xs bg-input-background border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value} className="text-xs">
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Filter Summary and Clear */}
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  {filteredCases.length} case{filteredCases.length !== 1 ? 's' : ''} found
                  {hasActiveFilters && (
                    <span className="ml-1">
                      • {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {hasActiveFilters && (
                  <Button
                    onClick={handleClearFilters}
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground transition-all duration-200"
                  >
                    <FilterX className="h-3 w-3 mr-1" />
                    Clear
                  </Button>
                )}
              </div>

              {/* Active Filter Badges */}
              {hasActiveFilters && (
                <div className="flex flex-wrap gap-1">
                  {expandedSearchTerm && (
                    <Badge variant="outline" className="text-xs px-2 py-0">
                      Search: "{expandedSearchTerm}"
                    </Badge>
                  )}
                  {typeFilter !== "all" && (
                    <Badge variant="outline" className="text-xs px-2 py-0">
                      Type: {caseTypeOptions.find(opt => opt.value === typeFilter)?.label}
                    </Badge>
                  )}
                  {statusFilter !== "all" && (
                    <Badge variant="outline" className="text-xs px-2 py-0">
                      Status: {statusOptions.find(opt => opt.value === statusFilter)?.label}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cases Section - Now takes up more space */}
        <div className="flex-1 p-4 border-t border-sidebar-border flex flex-col min-h-0">
          <Button
            variant="ghost"
            onClick={() => setIsWorkspaceOpen(!isWorkspaceOpen)}
            className="w-full justify-between text-sidebar-foreground hover:bg-sidebar-accent transition-all duration-200 hover:scale-[1.01] group mb-2"
          >
            <span className="text-sm font-medium">
              Cases ({displayedCases.length})
              {hasActiveFilters && (
                <span className="ml-1 text-xs text-muted-foreground">
                  of {cases.length}
                </span>
              )}
            </span>
            <div className="flex items-center gap-2">
              {hasActiveFilters && (
                <Badge variant="outline" className="text-xs px-1 py-0">
                  Filtered
                </Badge>
              )}
              <ChevronDown className={`h-4 w-4 transition-all duration-300 group-hover:scale-110 ${isWorkspaceOpen ? "rotate-180" : ""}`} />
            </div>
          </Button>
          
          {isWorkspaceOpen && (
            <div className="flex-1 space-y-1 animate-in slide-in-from-top-2 duration-200 overflow-y-auto min-h-0">
              {displayedCases.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-muted-foreground text-sm">
                    {hasActiveFilters ? "No cases match your filters" : "No cases yet"}
                  </div>
                  {hasActiveFilters ? (
                    <Button
                      variant="ghost"
                      onClick={handleClearFilters}
                      className="mt-2 text-xs text-muted-foreground hover:text-sidebar-foreground"
                    >
                      <FilterX className="h-3 w-3 mr-1" />
                      Clear filters
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      onClick={() => setIsCreateModalOpen(true)}
                      className="mt-2 text-xs text-muted-foreground hover:text-sidebar-foreground"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Create your first case
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {displayedCases.map((caseItem, index) => (
                    <ContextMenu key={caseItem.id}>
                      <ContextMenuTrigger asChild>
                        <Button
                          variant={activeCase?.id === caseItem.id ? "default" : "ghost"}
                          onClick={() => onSwitchCase(caseItem)}
                          className={`w-full justify-start text-xs h-auto py-3 px-3 transition-all duration-200 hover:translate-x-2 hover:scale-[1.01] group ${
                            activeCase?.id === caseItem.id 
                              ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm" 
                              : "text-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent"
                          }`}
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                          <div className="flex-1 text-left space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium transition-all duration-200 group-hover:text-sidebar-foreground truncate">
                                {caseItem.title}
                              </span>
                              <Badge 
                                className={`text-xs h-4 px-1.5 ${getCaseTypeColor(caseItem.type)}`}
                              >
                                {getCaseTypeLabel(caseItem.type)}
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {caseItem.createdAt.toLocaleDateString()}
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-muted-foreground">
                                  {caseItem.urls.length} URLs
                                </span>
                                <div className={`w-2 h-2 rounded-full ml-1 transition-all duration-200 group-hover:scale-125 status-dot ${caseItem.status} ${getStatusColor(caseItem.status)} shadow-sm`} />
                              </div>
                            </div>
                          </div>
                        </Button>
                      </ContextMenuTrigger>
                      <ContextMenuContent className="w-48">
                        <ContextMenuItem 
                          variant="destructive"
                          onClick={() => handleDeleteClick(caseItem)}
                          className="cursor-pointer"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Case
                        </ContextMenuItem>
                      </ContextMenuContent>
                    </ContextMenu>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-sidebar-border">
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onOpenSettings}
              className="flex-1 justify-start text-sidebar-foreground hover:bg-sidebar-accent transition-all duration-200 hover:translate-x-1 hover:scale-[1.01] group"
            >
              <Settings className="h-4 w-4 mr-2 transition-all duration-300 group-hover:rotate-45 group-hover:scale-110" />
              Settings
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-sidebar-foreground hover:bg-sidebar-accent transition-all duration-200 hover:scale-110 group relative"
            >
              <Bell className="h-4 w-4 transition-all duration-200 group-hover:animate-pulse" />
              {/* Notification dot */}
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
            </Button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Case</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the case "{caseToDelete?.title}"? This action cannot be undone and will permanently remove all case data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Case
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Case Modal */}
      <CreateCaseModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateCase={handleCreateCase}
      />
    </>
  );
}